import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';
import {
    defaultHRM3050_R01DetailDto, defaultHRM3050_R01HeaderDto, HRM3050_R01DetailDto, HRM3050_R01HeaderDto,
} from './Dto_HRM3050_R01';
import { HRM3050_R01GridType } from './Util_HRM3050_R01';

/** 근무조 목록·건수 조회에 전달하는 화면 검색 조건이다. */
export interface HRM3050_R01Filter {
    useYn: string; // 사용여부(Y/N). 빈 값이면 전체
}
/** 빈 문자열은 해당 검색 조건을 적용하지 않는 기본 상태를 뜻한다. */
export const defaultHRM3050_R01Filter: HRM3050_R01Filter = { useYn: '' };

/**
 * 근무조(Header)와 선택 근무조의 휴게(Detail) 상태를 관리한다.
 * 근무조는 한 번에 한 행만 선택하고, 저장도 선택 근무조 1건과 그 휴게 목록 단위로 한다.
 */
export interface Store_HRM3050_R01 {
    HRM3050_R01Filter: HRM3050_R01Filter;
    HRM3050_R01PastFilter: HRM3050_R01Filter;
    setHRM3050_R01Filter: (filter: Partial<HRM3050_R01Filter>) => void;
    setHRM3050_R01PastFilter: (filter: Partial<HRM3050_R01Filter>) => void;

    activeGridType: HRM3050_R01GridType; // 마지막으로 클릭한 그리드. 등록·삭제 버튼의 대상
    setActiveGridType: (gridType: HRM3050_R01GridType) => void;

    HRM3050_R01List: HRM3050_R01HeaderDto[];
    setHRM3050_R01List: (list: HRM3050_R01HeaderDto[]) => void;
    insertHRM3050_R01List: (data: HRM3050_R01HeaderDto[], index?: number) => void;
    updateHRM3050_R01List: (index: number, data: HRM3050_R01HeaderDto) => void;
    originalHRM3050_R01List: HRM3050_R01HeaderDto[];
    setOriginalHRM3050_R01List: (list: HRM3050_R01HeaderDto[]) => void;
    selectedHRM3050_R01: HRM3050_R01HeaderDto | undefined; // 현재 선택한 근무조. 휴게 조회·저장·엑셀의 대상
    setSelectedHRM3050_R01: (data: HRM3050_R01HeaderDto | undefined) => void;
    initSelectedHRM3050_R01: () => void;

    HRM3050_R01DetailList: HRM3050_R01DetailDto[];
    setHRM3050_R01DetailList: (list: HRM3050_R01DetailDto[]) => void;
    insertHRM3050_R01DetailList: (data: HRM3050_R01DetailDto[], index?: number) => void;
    removeHRM3050_R01DetailList: (index: number) => void;
    updateHRM3050_R01DetailList: (index: number, data: HRM3050_R01DetailDto) => void;
    selectedHRM3050_R01DetailList: string[];
    addSelectedHRM3050_R01DetailList: (ids: string[]) => void;
    removeSelectedHRM3050_R01DetailList: (ids?: string[]) => void;
    initSelectedHRM3050_R01DetailList: () => void;
    allSelectedHRM3050_R01DetailList: () => void;
    deletedHRM3050_R01DetailList: string[];
    addDeletedHRM3050_R01DetailList: (ids: string[]) => void;
    removeDeletedHRM3050_R01DetailList: (ids?: string[]) => void;
    originalHRM3050_R01DetailList: HRM3050_R01DetailDto[];
    setOriginalHRM3050_R01DetailList: (list: HRM3050_R01DetailDto[]) => void;

    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;
    detailColumnState: ColumnState[] | null;
    setDetailColumnState: (state: ColumnState[] | null) => void;
    scrollTop: number | null;
    setScrollTop: (top: number | null) => void;
    detailScrollTop: number | null;
    setDetailScrollTop: (top: number | null) => void;
    allCount: number;
    setAllCount: (count: number) => void;
    initData: () => void;
}

/**
 * 서버 행의 누락된 화면 기본값과 그리드 식별·정렬 인덱스를 채운다. 두 그리드 목록이 같은 규칙을 쓴다.
 * @param list 서버 응답 또는 화면 목록
 * @param defaultRow 누락 필드를 채울 기본값
 * @returns 이미 부여된 valueIndex는 유지하고, 없으면 1부터 부여한 새 목록
 */
const buildGridList = <T extends HRM3050_R01HeaderDto | HRM3050_R01DetailDto>(list: T[], defaultRow: T): T[] =>
    list.map((item, index) => ({
        ...defaultRow, ...item,
        valueIndex: item.valueIndex > 0 ? item.valueIndex : index + 1,
        orderIndex: item.orderIndex > 0 ? item.orderIndex : index + 1,
        orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0,
    }));

/**
 * 서버 조회 원본에 안정적인 인덱스를 부여한다. 변경 비교와 변경 취소(원래 값 복원)에 쓴다.
 * @returns valueIndex·orderIndex를 1부터 다시 매긴 새 목록
 */
const buildOriginalList = <T extends HRM3050_R01HeaderDto | HRM3050_R01DetailDto>(list: T[], defaultRow: T): T[] =>
    list.map((item, index) => ({
        ...defaultRow, ...item,
        valueIndex: index + 1, orderIndex: index + 1, orderIndexSeq: 0,
    }));

/**
 * 신규 행에 그리드 안에서 고유한 valueIndex와 기준 행의 정렬 인덱스를 부여해 삽입한다.
 * index가 있으면 해당 행 다음에 삽입한다. 기준 행을 찾지 못하면 맨 앞에, index가 없으면 맨 뒤에 넣는다. (HRM3110_R01과 동일)
 * @returns 신규 행이 들어간 새 목록
 */
const insertGridRows = <T extends HRM3050_R01HeaderDto | HRM3050_R01DetailDto>(
    currentList: T[], data: T[], defaultRow: T, index?: number,
): T[] => {
    const list = [...currentList];
    const target = list.find(item => item.valueIndex === index);
    // ??: 기준 행이 없으면 정렬 인덱스를 0부터 시작한다.
    const orderIndex = target?.orderIndex ?? 0;
    const orderIndexSeq = (target?.orderIndexSeq ?? 0) + 1;
    const maxIndex = Math.max(0, ...list.map(item => item.valueIndex));
    const newRows = data.map((item, offset) => ({
        ...defaultRow, ...item,
        valueIndex: maxIndex + offset + 1,
        orderIndex,
        orderIndexSeq,
    }));
    // 기준 행이 전달되면 해당 행 바로 뒤에 삽입한다.
    if (index !== undefined) {
        list.splice(Math.max(0, list.findIndex(item => item.valueIndex === index) + 1), 0, ...newRows);
    } else {
        // 기준 행이 없으면 신규 행을 목록 끝에 추가한다.
        list.push(...newRows);
    }
    return list;
};

/**
 * 식별값 목록에서 지정한 값만 지우거나, ids가 없으면 전체를 비운다.
 * @returns 남은 식별값 목록
 */
const removeIds = (currentIds: string[], ids?: string[]) => currentIds.filter(id => ids ? !ids.includes(id) : false);

/**
 * HRM3050_R01 화면 전용 Store이다. 각 action은 반환값 없이 지정한 상태만 갱신한다.
 * 조회·저장 API 호출은 Content에서 수행한다.
 */
export const useHRM3050_R01Store = create<Store_HRM3050_R01>((set) => ({
    HRM3050_R01Filter: { ...defaultHRM3050_R01Filter },
    HRM3050_R01PastFilter: { ...defaultHRM3050_R01Filter },
    /** 전달된 검색 필드만 현재 조건에 병합한다. */
    setHRM3050_R01Filter: (filter) => set(state => ({ HRM3050_R01Filter: { ...state.HRM3050_R01Filter, ...filter } })),
    /** 마지막 조회에 사용한 검색 조건을 부분 갱신한다. */
    setHRM3050_R01PastFilter: (filter) => set(state => ({ HRM3050_R01PastFilter: { ...state.HRM3050_R01PastFilter, ...filter } })),

    activeGridType: 'detail',
    /**
     * 등록·삭제 버튼이 적용될 그리드를 바꾼다. 값이 같으면 기존 상태를 그대로 돌려줘 화면을 다시 그리지 않는다.
     * 그리드 영역을 클릭할 때마다 호출되므로, 매번 다시 그리면 그리드 컬럼 정의가 새로 만들어져 셀 편집이 끊길 수 있다.
     */
    // 삼항 연산자: 같은 그리드면 현재 상태 객체를 그대로, 다르면 바뀐 값만 반환한다.
    setActiveGridType: (activeGridType) => set(state => state.activeGridType === activeGridType ? state : { activeGridType }),

    HRM3050_R01List: [],
    /** 근무조 목록을 인덱스를 채워 저장한다. */
    setHRM3050_R01List: (list) => set({ HRM3050_R01List: buildGridList(list, defaultHRM3050_R01HeaderDto) }),
    /** 근무조 그리드에 신규 행을 삽입한다. */
    insertHRM3050_R01List: (data, index) => set(state => ({
        HRM3050_R01List: insertGridRows(state.HRM3050_R01List, data, defaultHRM3050_R01HeaderDto, index),
    })),
    /** 근무조 행을 교체한다. 선택 근무조와 같은 행이면 선택 값도 같이 바꿔 휴게 요약·저장에 최신 값이 쓰이게 한다. */
    updateHRM3050_R01List: (index, data) => set(state => ({
        HRM3050_R01List: state.HRM3050_R01List.map(item => item.valueIndex === index ? data : item),
        // 삼항 연산자: 선택 행을 고친 경우에만 선택 값을 새 행으로 바꾸고, 아니면 그대로 둔다.
        selectedHRM3050_R01: state.selectedHRM3050_R01?.valueIndex === index ? data : state.selectedHRM3050_R01,
    })),
    originalHRM3050_R01List: [],
    /** 근무조 서버 조회 원본을 변경 비교·복원용으로 저장한다. */
    setOriginalHRM3050_R01List: (list) => set({ originalHRM3050_R01List: buildOriginalList(list, defaultHRM3050_R01HeaderDto) }),
    selectedHRM3050_R01: undefined,
    /** 선택 근무조를 바꾼다. */
    setSelectedHRM3050_R01: (selectedHRM3050_R01) => set({ selectedHRM3050_R01 }),
    /** 근무조 선택을 해제한다. */
    initSelectedHRM3050_R01: () => set({ selectedHRM3050_R01: undefined }),

    HRM3050_R01DetailList: [],
    /** 휴게 목록을 인덱스를 채워 저장한다. */
    setHRM3050_R01DetailList: (list) => set({ HRM3050_R01DetailList: buildGridList(list, defaultHRM3050_R01DetailDto) }),
    /** 휴게 그리드에 신규 행을 삽입한다. */
    insertHRM3050_R01DetailList: (data, index) => set(state => ({
        HRM3050_R01DetailList: insertGridRows(state.HRM3050_R01DetailList, data, defaultHRM3050_R01DetailDto, index),
    })),
    /** valueIndex로 휴게 신규 행을 제거하고 선택·삭제 목록에서도 같은 식별값을 지운다. */
    removeHRM3050_R01DetailList: (index) => set(state => ({
        HRM3050_R01DetailList: state.HRM3050_R01DetailList.filter(item => item.valueIndex !== index),
        // 목록에 숫자와 문자열 식별값이 섞일 수 있어 양쪽을 문자열로 맞춰 비교한다.
        selectedHRM3050_R01DetailList: state.selectedHRM3050_R01DetailList.filter(id => String(id) !== String(index)),
        deletedHRM3050_R01DetailList: state.deletedHRM3050_R01DetailList.filter(id => String(id) !== String(index)),
    })),
    /** 휴게 그리드의 해당 valueIndex 행을 교체한다. */
    updateHRM3050_R01DetailList: (index, data) => set(state => ({
        HRM3050_R01DetailList: state.HRM3050_R01DetailList.map(item => item.valueIndex === index ? data : item),
    })),
    selectedHRM3050_R01DetailList: [],
    /** 휴게 그리드의 새 선택 식별값을 중복 없이 합친다. */
    addSelectedHRM3050_R01DetailList: (ids) => set(state => ({
        selectedHRM3050_R01DetailList: Array.from(new Set([...state.selectedHRM3050_R01DetailList, ...ids])),
    })),
    /** ids가 있으면 해당 선택만 제거하고, 없으면 휴게 그리드 선택을 모두 해제한다. */
    removeSelectedHRM3050_R01DetailList: (ids) => set(state => ({
        selectedHRM3050_R01DetailList: removeIds(state.selectedHRM3050_R01DetailList, ids),
    })),
    /** 휴게 그리드 선택 목록을 빈 배열로 초기화한다. */
    initSelectedHRM3050_R01DetailList: () => set({ selectedHRM3050_R01DetailList: [] }),
    /** 휴게 그리드의 모든 valueIndex를 문자열 식별값으로 선택한다. */
    allSelectedHRM3050_R01DetailList: () => set(state => ({
        selectedHRM3050_R01DetailList: state.HRM3050_R01DetailList.map(item => String(item.valueIndex)),
    })),
    deletedHRM3050_R01DetailList: [],
    /** 저장 시 삭제할 기존 휴게 행의 식별값을 중복 없이 추가한다. */
    addDeletedHRM3050_R01DetailList: (ids) => set(state => ({
        deletedHRM3050_R01DetailList: Array.from(new Set([...state.deletedHRM3050_R01DetailList, ...ids])),
    })),
    /** ids가 있으면 지정된 삭제 표시만, 없으면 휴게 그리드 삭제 표시 전체를 지운다. */
    removeDeletedHRM3050_R01DetailList: (ids) => set(state => ({
        deletedHRM3050_R01DetailList: removeIds(state.deletedHRM3050_R01DetailList, ids),
    })),
    originalHRM3050_R01DetailList: [],
    /** 휴게 서버 조회 원본을 변경 비교용으로 저장한다. */
    setOriginalHRM3050_R01DetailList: (list) => set({
        originalHRM3050_R01DetailList: buildOriginalList(list, defaultHRM3050_R01DetailDto),
    }),

    columnState: null,
    /** 근무조 그리드 컬럼 순서·너비·표시 상태를 저장한다. */
    setColumnState: (columnState) => set({ columnState }),
    detailColumnState: null,
    /** 휴게 그리드 컬럼 순서·너비·표시 상태를 저장한다. */
    setDetailColumnState: (detailColumnState) => set({ detailColumnState }),
    scrollTop: null,
    /** 조회 또는 탭 복귀에 사용할 근무조 그리드 스크롤 위치를 저장한다. */
    setScrollTop: (scrollTop) => set({ scrollTop }),
    detailScrollTop: null,
    /** 조회 또는 탭 복귀에 사용할 휴게 그리드 스크롤 위치를 저장한다. */
    setDetailScrollTop: (detailScrollTop) => set({ detailScrollTop }),
    allCount: 0,
    /** 현재 검색 조건에 맞는 근무조 전체 건수를 저장한다. */
    setAllCount: (allCount) => set({ allCount }),
    /** 필터, 두 그리드의 목록·선택·삭제·원본, 대상 그리드, 컬럼 상태, 스크롤, 건수를 초기화한다. */
    initData: () => set({
        HRM3050_R01Filter: { ...defaultHRM3050_R01Filter },
        HRM3050_R01PastFilter: { ...defaultHRM3050_R01Filter },
        activeGridType: 'detail',
        HRM3050_R01List: [],
        originalHRM3050_R01List: [],
        selectedHRM3050_R01: undefined,
        HRM3050_R01DetailList: [],
        selectedHRM3050_R01DetailList: [],
        deletedHRM3050_R01DetailList: [],
        originalHRM3050_R01DetailList: [],
        columnState: null,
        detailColumnState: null,
        scrollTop: null,
        detailScrollTop: null,
        allCount: 0,
    }),
}));
